import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { CardApi } from '../tokens/card.token';
import {
  CardFixedLimits,
  UpdateCardLimits,
  CardReplacementResult,
  CardRevealToken,
  CardTier,
  CardTransactionPage,
  CardTransactionQuery,
  ChangePinRequest,
  CreateTempLimitRequest,
  DisputeResult,
  DisputeTransactionRequest,
  Enrol3dsRequest,
  FreezeCardRequest,
  ReplaceCardRequest,
  SmeCard,
  TempLimit,
  ThreeDsStatus,
} from '../models/card.model';

/**
 * REST-only implementation of `CardApi` (EPIC-3 — EM-413, K1.4).
 *
 * Calls the embedded business card surface:
 *   GET  /services/embedded/api/sme-cards/{cardId}
 *   POST /services/embedded/api/sme-cards/{cardId}/freeze
 *   POST /services/embedded/api/sme-cards/{cardId}/unfreeze
 *
 * Rides the existing Keycloak bearer (attached by `authInterceptor`). This is the shared card
 * data-service every EPIC-3 desktop/mobile screen consumes; later K-stories (K2–K8) APPEND methods
 * here (reveal token, limits, dispute, tier, replace) rather than forking it (Rule 17).
 *
 * Rule 07 / Rule 10 — no Mock fallback; Playwright specs intercept via `page.route()`. Error
 * surfacing (Rule 09) is the caller's responsibility via `extractBackendErrorMessage` — this
 * service propagates the raw `HttpErrorResponse`.
 */
@Injectable()
export class CardRestService implements CardApi {
  private readonly http = inject(HttpClient);
  private readonly base = API_PATHS.CARDS;

  /**
   * GET the active SME party's own masked card (EM-421, WD1). The desktop View sub-tab has no Grace
   * card id to begin with, so it discovers the card via `GET /sme-cards/me` (the literal `/me` is
   * matched ahead of `/{cardId}` backend-side). No SME party → 403 (surfaced with
   * `extractBackendErrorMessage`, Rule 09). A party with no card yet returns an empty 200 → `null`,
   * which the caller renders as a "no card" state rather than an error.
   */
  getMyCard(): Observable<SmeCard | null> {
    return this.http.get<SmeCard | null>(`${this.base}/me`);
  }

  getCard(cardId: string): Observable<SmeCard> {
    return this.http.get<SmeCard>(`${this.base}/${encodeURIComponent(cardId)}`);
  }

  /** C-03 freeze — SCA-gated: the `freeze_card` token rides the dedicated `X-SCA-Token` header. */
  freeze(cardId: string, scaToken: string, body?: FreezeCardRequest): Observable<SmeCard> {
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.post<SmeCard>(`${this.base}/${encodeURIComponent(cardId)}/freeze`, body ?? {}, { headers });
  }

  /** C-03 unfreeze — SCA-gated: the `freeze_card` token rides the dedicated `X-SCA-Token` header. */
  unfreeze(cardId: string, scaToken: string): Observable<SmeCard> {
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.post<SmeCard>(`${this.base}/${encodeURIComponent(cardId)}/unfreeze`, {}, { headers });
  }

  /**
   * C-02 reveal (K2 — EM-414). Attaches the minted `reveal_card` SCA token on the dedicated
   * `X-SCA-Token` header (NEVER `Authorization`, which stays the Keycloak bearer). The response is a
   * reveal token + widget mount info — never the PAN/CVV.
   */
  revealDetails(cardId: string, scaToken: string): Observable<CardRevealToken> {
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.post<CardRevealToken>(
      `${this.base}/${encodeURIComponent(cardId)}/details`,
      {},
      { headers },
    );
  }

  /**
   * C-09 replace (K8 — EM-420). Owner-only on the backend; re-issues the card via Grace and returns
   * the new card id + estimated delivery instant. Errors propagate raw for the caller to surface
   * with `extractBackendErrorMessage` (Rule 09).
   */
  replaceCard(cardId: string, request: ReplaceCardRequest): Observable<CardReplacementResult> {
    return this.http.post<CardReplacementResult>(
      `${this.base}/${encodeURIComponent(cardId)}/replace`,
      request,
    );
  }

  /**
   * C-06 fixed limits (EM-422). The card's REAL always-on permanent limits per channel, read straight
   * from Grace for the "Fixed limits" panel — no fabricated ceilings (Rule 09).
   */
  getFixedLimits(cardId: string): Observable<CardFixedLimits> {
    return this.http.get<CardFixedLimits>(`${this.base}/${encodeURIComponent(cardId)}/limits`);
  }

  /**
   * C-06 edit limits (EM-422). SCA-gated: the `edit_card_limits` token rides the `X-SCA-Token` header.
   * Pushes the edited per-channel ceilings to Grace and returns the refreshed limits.
   */
  updateFixedLimits(cardId: string, payload: UpdateCardLimits, scaToken: string): Observable<CardFixedLimits> {
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.put<CardFixedLimits>(`${this.base}/${encodeURIComponent(cardId)}/limits`, payload, { headers });
  }

  /**
   * C-06 temporary limits (K3 — EM-415). The active ledger for the Limits sub-tab (WD2). Owner/CFO
   * only on the backend; errors propagate raw for the caller to surface (Rule 09).
   */
  listTempLimits(cardId: string): Observable<TempLimit[]> {
    return this.http.get<TempLimit[]>(`${this.base}/${encodeURIComponent(cardId)}/temp-limits`);
  }

  /**
   * Adds a temporary limit. The backend rejects the 7th active limit with `422` (§6.0.3) — the raw
   * `HttpErrorResponse` propagates so the caller surfaces the message with `extractBackendErrorMessage`.
   */
  addTempLimit(cardId: string, request: CreateTempLimitRequest): Observable<TempLimit> {
    return this.http.post<TempLimit>(
      `${this.base}/${encodeURIComponent(cardId)}/temp-limits`,
      request,
    );
  }

  /** Cancels an active temporary limit before expiry. */
  cancelTempLimit(cardId: string, limitId: number): Observable<TempLimit> {
    return this.http.delete<TempLimit>(
      `${this.base}/${encodeURIComponent(cardId)}/temp-limits/${encodeURIComponent(String(limitId))}`,
    );
  }

  /**
   * C-07 transactions (K4 — EM-416). Lists a card's transactions, optionally filtered by C-07
   * status, with cursor/limit paging. Declined transactions are returned (they feed the R-09 badge).
   */
  getTransactions(cardId: string, query?: CardTransactionQuery): Observable<CardTransactionPage> {
    let params = new HttpParams();
    if (query?.status) {
      params = params.set('status', query.status);
    }
    if (query?.cursor) {
      params = params.set('cursor', query.cursor);
    }
    if (query?.limit != null) {
      params = params.set('limit', String(query.limit));
    }
    return this.http.get<CardTransactionPage>(
      `${this.base}/${encodeURIComponent(cardId)}/transactions`,
      { params },
    );
  }

  /**
   * C-07 dispute (K4 — EM-416). Opens a dispute against one of the card's transactions; any rep.
   * `cardId` scopes ownership (txId→card→party). Returns `{disputeId, status:'submitted'}`; errors
   * propagate raw for the caller to surface with `extractBackendErrorMessage` (Rule 09).
   */
  disputeTransaction(
    cardId: string,
    txId: string,
    request: DisputeTransactionRequest,
  ): Observable<DisputeResult> {
    return this.http.post<DisputeResult>(
      `${this.base}/${encodeURIComponent(cardId)}/transactions/${encodeURIComponent(txId)}/dispute`,
      request,
    );
  }

  /**
   * C-04 change PIN (K5 — EM-417). Attaches the minted `change_pin` SCA token on the dedicated
   * `X-SCA-Token` header (NEVER `Authorization`, which stays the Keycloak bearer). The `newPin` is in
   * the body over the PCI-compliant TLS channel. DEFERRED skeleton: the backend rejects with `403
   * feature_disabled` until a Grace PIN surface lands; the raw `HttpErrorResponse` propagates so the
   * caller surfaces the message with `extractBackendErrorMessage` (Rule 09). The UI is mobile-only
   * (desktop redirects to mobile per V1.4.0; the mobile screen is M1, spec-only).
   */
  changePin(cardId: string, scaToken: string, request: ChangePinRequest): Observable<void> {
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.post<void>(`${this.base}/${encodeURIComponent(cardId)}/pin`, request, { headers });
  }

  /**
   * C-05 3DS state (K6 — EM-418). DEFERRED skeleton: the backend returns `{enrolled:false}` (no error)
   * until Grace adds a 3DS surface. Any rep; errors propagate raw for the caller to surface (Rule 09).
   */
  get3ds(cardId: string): Observable<ThreeDsStatus> {
    return this.http.get<ThreeDsStatus>(`${this.base}/${encodeURIComponent(cardId)}/3ds`);
  }

  /**
   * C-05 3DS enrol (K6 — EM-418). Attaches the minted `enrol_3ds` SCA token on the dedicated
   * `X-SCA-Token` header (NEVER `Authorization`, which stays the Keycloak bearer). DEFERRED skeleton:
   * the backend rejects with `403 feature_disabled` until a Grace 3DS surface lands; the raw
   * `HttpErrorResponse` propagates so the caller surfaces the message with `extractBackendErrorMessage`
   * (Rule 09). The Grace device block/unblock APIs are never bent into enrolment.
   */
  enrol3ds(cardId: string, scaToken: string, request?: Enrol3dsRequest): Observable<ThreeDsStatus> {
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.post<ThreeDsStatus>(
      `${this.base}/${encodeURIComponent(cardId)}/3ds/enrol`,
      request ?? {},
      { headers },
    );
  }

  /** C-05 3DS re-enrol (K6 — EM-418). Same contract as {@link enrol3ds}. */
  reEnrol3ds(cardId: string, scaToken: string, request?: Enrol3dsRequest): Observable<ThreeDsStatus> {
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.post<ThreeDsStatus>(
      `${this.base}/${encodeURIComponent(cardId)}/3ds/re-enrol`,
      request ?? {},
      { headers },
    );
  }

  /**
   * C-08 tier (K7 — EM-419). Any rep of the owning party; read-only, no audit. Returns the current
   * tier + the next tier the card is eligible for (with ETA + criteria) + the next-review date. The
   * Tier Review sub-tab (WD2) consumes this; errors propagate raw for the caller to surface with
   * `extractBackendErrorMessage` (Rule 09).
   */
  getTier(cardId: string): Observable<CardTier> {
    return this.http.get<CardTier>(`${this.base}/${encodeURIComponent(cardId)}/tier`);
  }
}
